import { cn, IssueIcon, Modal, Select, SpinnerWithBackground, Text, UploadIcon } from '@viapeople/components'
import type React from 'react'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { useToast } from '../../utils/ToastContext'
import type { FileUploadModalProps } from './types'

enum UploadStep {
	SelectFile = 0,
	MapColumns = 1,
	Validate = 2,
	Processing = 3,
}

export const FileUploadModal = ({
	isOpen,
	onClose,
	onUploadSuccess,
	fileConfig,
	columnMappingFields,
	uploadRequest,
	title = 'Upload File',
	overwriteMessage,
	renderBeforeColumnMapping,
	enableValidation,
	validationColumnMappedFields = [],
}: FileUploadModalProps) => {
	const [currentStep, setCurrentStep] = useState<UploadStep>(UploadStep.SelectFile)
	const [file, setFile] = useState<File | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [headers, setHeaders] = useState<string[]>([])
	const [fileGroups, setFileGroups] = useState<string[]>([])
	const [columnMapping, setColumnMapping] = useState<Record<string, string>>({})
	const [groupsMapping, setGroupsMapping] = useState<Record<string, string>>({})
	const [fileId, setFileId] = useState<string>('')
	const { showToast } = useToast()

	const fileInputRef = useRef<HTMLInputElement>(null)
	const dropAreaRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (isOpen) {
			setCurrentStep(UploadStep.SelectFile)
			setFile(null)
			setHeaders([])
			setFileGroups([])
			setColumnMapping({})
			setGroupsMapping({})
			setFileId('')
			setError(null)
		}
	}, [isOpen])

	const handleClose = () => {
		onClose()
	}

	const isColumnMappingComplete = () => {
		return columnMappingFields.filter((field) => field.required).every((field) => columnMapping[field.key])
	}

	const isGroupsMappingComplete = () => {
		return fileGroups.length === 0 || fileGroups.every((group) => groupsMapping[group])
	}

	const hoverDragArea = (hover: boolean) => {
		if (dropAreaRef.current) {
			if (hover) {
				dropAreaRef.current.classList.remove('bg-neutral-50')
				dropAreaRef.current.classList.add('bg-neutral-200')
			} else {
				dropAreaRef.current.classList.remove('bg-neutral-200')
				dropAreaRef.current.classList.add('bg-neutral-50')
			}
		}
	}

	const validateAndSetFile = async (file: File) => {
		setError(null)

		if (!fileConfig.allowedTypes.includes(file.type)) {
			const errorMsg = `Invalid file type. Please upload a ${fileConfig.extensions} file.`
			setError(errorMsg)
			showToast(errorMsg, 'error')
			return
		}

		if (fileConfig.maxSize && file.size > fileConfig.maxSize) {
			const errorMsg = `File size exceeds ${fileConfig.maxSize / (1024 * 1024)}MB limit.`
			setError(errorMsg)
			showToast(errorMsg, 'error')
			return
		}

		setFile(file)
		setCurrentStep(UploadStep.Processing)

		try {
			const { fileId, uploadUrl } = await uploadRequest.requestUpload({
				filename: file.name,
				mimetype: file.type,
				size: file.size,
			})

			setFileId(fileId)

			const response = await fetch(uploadUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			})

			if (!response.ok) {
				throw new Error('Failed to upload file')
			}

			const { headers: fileHeaders } = await uploadRequest.acknowledgeUpload({
				fileId,
				filename: file.name,
			})

			setHeaders(fileHeaders)
			setCurrentStep(UploadStep.MapColumns)
		} catch (err) {
			const errorMsg = (err as Error).message || 'Failed to upload file. Please try again.'
			setError(errorMsg)
			showToast(errorMsg, 'error')
			setCurrentStep(UploadStep.SelectFile)
		}
	}

	const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
		e.preventDefault()
		e.stopPropagation()
		const files = e.dataTransfer.files
		hoverDragArea(false)
		if (files && files.length > 0) {
			validateAndSetFile(files[0])
		}
	}

	const onDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
		e.preventDefault()
		e.stopPropagation()
		hoverDragArea(true)
	}

	const onDragLeave = (e: React.DragEvent<HTMLButtonElement>) => {
		e.preventDefault()
		e.stopPropagation()
		hoverDragArea(false)
	}

	const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files
		if (files && files.length > 0) {
			validateAndSetFile(files[0])
		}
	}

	const handleColumnMappingChange = (field: string, value: string) => {
		setColumnMapping((prev) => ({
			...prev,
			[field]: value,
		}))
	}

	const handleGroupsMappingChange = (group: string, value: string) => {
		setGroupsMapping((prev) => ({
			...prev,
			[group]: value,
		}))
	}

	const handleNext = async () => {
		if (!file || !fileId) return

		if (currentStep === UploadStep.MapColumns) {
			if (!isColumnMappingComplete()) {
				const errorMsg = 'Please map all required fields.'
				setError(errorMsg)
				showToast(errorMsg, 'error')
				return
			}

			if (enableValidation && uploadRequest.validateFile) {
				setCurrentStep(UploadStep.Processing)
				try {
					const result = await uploadRequest.validateFile({
						fileId,
						columnMapping,
					})

					setFileGroups(result.groups)

					if (result.isValid) {
						if (validationColumnMappedFields.length > 0 && result.groups.length > 0) {
							setCurrentStep(UploadStep.Validate)
						}
					} else {
						setError(result.errors.join('. '))
						showToast(result.errors.join('. '), 'error')
						setCurrentStep(UploadStep.MapColumns)
					}
				} catch (err) {
					const errorMsg = (err as Error).message || 'Failed to validate file. Please try again.'
					setError(errorMsg)
					showToast(errorMsg, 'error')
					setCurrentStep(UploadStep.MapColumns)
				}
			} else {
				await processFile()
			}
		} else if (currentStep === UploadStep.Validate) {
			if (!isGroupsMappingComplete()) {
				const errorMsg = 'Please map all required groups.'
				setError(errorMsg)
				showToast(errorMsg, 'error')
				return
			}

			await processFile()
		}
	}

	const processFile = async () => {
		setCurrentStep(UploadStep.Processing)
		try {
			await uploadRequest.processFile({
				fileId,
				columnMapping,
				groupsMapping: Object.keys(groupsMapping).length > 0 ? groupsMapping : undefined,
			})

			onUploadSuccess()
			handleClose()
		} catch (err) {
			const errorMsg = (err as Error).message || 'Failed to process file. Please try again.'
			setError(errorMsg)
			showToast(errorMsg, 'error')
			setCurrentStep(currentStep === UploadStep.Validate ? UploadStep.Validate : UploadStep.MapColumns)
		}
	}

	const renderFileSelectionStep = () => (
		<div className='flex flex-col items-center'>
			<button
				type='button'
				ref={dropAreaRef}
				className={cn(
					'w-full flex flex-col items-center justify-center rounded-xl p-8 bg-neutral-50 text-center cursor-pointer',
					error && 'bg-error-50',
				)}
				onClick={() => fileInputRef.current?.click()}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={handleDrop}>
				{error ? (
					<IssueIcon size={32} className='fill-error-600 mb-4' />
				) : (
					<UploadIcon size={32} className='fill-neutral-500 mb-4' />
				)}
				<Text variant='small' weight='semibold' className='mb-1'>
					{error ? 'Upload Error' : 'Upload your file'}
				</Text>
				<Text variant='small' className='text-neutral-500 mb-4'>
					{error ? error : 'Drag and drop your file here.'}
					<br />
					{error
						? 'Please try again.'
						: fileConfig.maxSize
							? `Max file size is ${fileConfig.maxSize / (1024 * 1024)}MB.`
							: ''}
				</Text>

				<input
					ref={fileInputRef}
					type='file'
					accept={fileConfig.extensions}
					onChange={handleFileInputChange}
					className='hidden'
				/>
				<Text variant='xsmall' weight='semibold' className='text-primary-600'>
					Upload from device
				</Text>
			</button>
		</div>
	)

	const headerOptions = headers.map((header) => ({ value: header, label: header }))

	const renderColumnMappingStep = () => (
		<div className='w-full'>
			<Text variant='xsmall' weight='semibold' className='mb-1'>
				File successfully uploaded
			</Text>
			<Text variant='xsmall' className='text-neutral-500 mb-6'>
				{file?.name}
			</Text>

			{renderBeforeColumnMapping?.()}

			<div className='space-y-4'>
				{columnMappingFields.map((field) => (
					<Select
						key={field.key}
						label={`Select ${field.label.toLowerCase()} header`}
						options={headerOptions}
						value={columnMapping[field.key] || ''}
						onChange={(value) => handleColumnMappingChange(field.key, value)}
						placeholder='Select column'
					/>
				))}
			</div>
			{overwriteMessage && (
				<Text variant='small' className='text-neutral-700 mt-4'>
					* {overwriteMessage}
				</Text>
			)}
		</div>
	)

	const renderValidateStep = () => (
		<div className='w-full'>
			<Text variant='xsmall' weight='semibold' className='mb-1'>
				File validation successful
			</Text>
			<Text variant='xsmall' className='text-neutral-500 mb-6'>
				Please map the reviewer groups from your file
			</Text>

			<div className='space-y-4'>
				{fileGroups.map((group) => (
					<Select
						key={group}
						label={`Map "${group}" to reviewer group`}
						options={[{ value: '', label: 'Select group' }].concat(
							validationColumnMappedFields.map((rg) => ({ value: rg.id, label: rg.name })),
						)}
						value={groupsMapping[group] || ''}
						onChange={(value) => handleGroupsMappingChange(group, value)}
						placeholder='Select reviewer group'
					/>
				))}
			</div>
		</div>
	)

	const renderProcessingStep = () => (
		<div className='w-full text-center relative h-56'>
			<div className='w-full h-full relative'>
				<SpinnerWithBackground />
			</div>
			<Text
				variant='xsmall'
				weight='semibold'
				className='text-primary-600 z-11 self-center absolute top-3/4 left-1/2 -translate-x-1/2'>
				Processing your file...
			</Text>
		</div>
	)

	const renderStep = () => {
		switch (currentStep) {
			case UploadStep.SelectFile:
				return renderFileSelectionStep()
			case UploadStep.MapColumns:
				return renderColumnMappingStep()
			case UploadStep.Validate:
				return renderValidateStep()
			case UploadStep.Processing:
				return renderProcessingStep()
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title={title}
			buttons={[
				{
					children: 'Cancel',
					onClick: handleClose,
					variant: 'secondary',
					disabled: currentStep === UploadStep.Processing,
				},
				{
					children: 'Next',
					onClick: handleNext,
					variant: 'primary',
					disabled:
						currentStep === UploadStep.Processing ||
						currentStep === UploadStep.SelectFile ||
						(currentStep === UploadStep.MapColumns && !isColumnMappingComplete()) ||
						(currentStep === UploadStep.Validate && !isGroupsMappingComplete()),
				},
			]}>
			{renderStep()}
		</Modal>
	)
}
